import '@awesome.me/webawesome-pro/dist/translations/uk.js'
import './components/mention'
import './components/stepper'

import webAwesomeSystemIconLibrary from '@awesome.me/webawesome-pro/dist/components/icon/library.system.js'
import {
  registerIconLibrary as registerWebAwesomeIconLibrary,
  unregisterIconLibrary as unregisterWebAwesomeIconLibrary
} from '@awesome.me/webawesome-pro/dist/webawesome.js'
import { forEach } from 'remeda'
import { iconSourceUrl } from './helpers/icon'
import {
  handleWebAwesomeIconError,
  WEB_AWESOME_SYSTEM_FALLBACK_LIBRARY
} from './helpers/icon-fallback'
import { resolveIcon, resolveSystemIcon } from './helpers/icon-source'
import { syncTreeExpansion } from './helpers/tree'
import { loadPageComponents } from './helpers/webawesome-components'
import { setupWebAwesomeUpdateWarnings } from './helpers/webawesome-update-warnings'

// Public types ----------------------------------------------------------------

export type { LivePropsAdapter } from './helpers/live-props'

// Constants -------------------------------------------------------------------

const PINNED_WEB_AWESOME_UPDATE_WARNING_COMPONENTS: ReadonlySet<string> =
  new Set(['wa-combobox', 'wa-date-input'])

// Private functions -----------------------------------------------------------

// Seeds the responsive page mode before Web Awesome renders its shadow DOM.
const seedPageView = (page: Element): void => {
  const breakpoint = page.getAttribute('mobile-breakpoint') ?? '768px'
  const view = window.matchMedia(`(width < ${breakpoint})`).matches
    ? 'mobile'
    : 'desktop'

  page.setAttribute('view', view)
}

// Resolves one Phosphor icon name to its self-hosted asset URL.
const resolvePhosphorIcon = (name: string): string =>
  iconSourceUrl(resolveIcon(name))

// Maps Web Awesome system names onto their Phosphor equivalents.
const resolveWebAwesomeSystemIcon = (
  name: string,
  _family?: string,
  variant?: string
): string => iconSourceUrl(resolveSystemIcon(name, variant))

// Public functions ------------------------------------------------------------

export { bindLiveProps } from './helpers/live-props'

// Registers Facade's icon libraries and global component integrations.
export const initFacade = (): void => {
  // Keep first-party and Web Awesome component chrome on the same self-hosted
  // Phosphor set, without falling back to Font Awesome's remote library.
  unregisterWebAwesomeIconLibrary('default')

  registerWebAwesomeIconLibrary('phosphor', {
    resolver: resolvePhosphorIcon
  })

  registerWebAwesomeIconLibrary(WEB_AWESOME_SYSTEM_FALLBACK_LIBRARY, {
    resolver: webAwesomeSystemIconLibrary.resolver,
    mutator: webAwesomeSystemIconLibrary.mutator,
    spriteSheet: webAwesomeSystemIconLibrary.spriteSheet
  })

  // The stable listener identity makes repeated initialization idempotent.
  document.addEventListener('wa-error', handleWebAwesomeIconError)

  registerWebAwesomeIconLibrary('system', {
    resolver: resolveWebAwesomeSystemIcon
  })
}

export * as FacadeHooks from './hooks'

// Module setup ----------------------------------------------------------------

// Register libraries before any dynamic component definition can render an icon.
initFacade()

// WA Page defaults to desktop and only evaluates its breakpoint after a
// deferred ResizeObserver callback. Seed the reflected property before the
// element is registered so its first shadow-DOM render uses the right view.
forEach(
  Array.from(
    document.querySelectorAll('wa-page[data-component=".page"].b2b-page')
  ),
  seedPageView
)

const cleanupPinnedWebAwesomeUpdateWarnings = setupWebAwesomeUpdateWarnings(
  PINNED_WEB_AWESOME_UPDATE_WARNING_COMPONENTS
)
const componentDefinitions = Promise.all([
  loadPageComponents(document),
  import('@awesome.me/webawesome-pro/dist/components/avatar/avatar.js'),
  import('@awesome.me/webawesome-pro/dist/components/badge/badge.js'),
  import('@awesome.me/webawesome-pro/dist/components/checkbox/checkbox.js'),
  import('@awesome.me/webawesome-pro/dist/components/combobox/combobox.js'),
  import('@awesome.me/webawesome-pro/dist/components/dialog/dialog.js'),
  import('@awesome.me/webawesome-pro/dist/components/divider/divider.js'),
  import('@awesome.me/webawesome-pro/dist/components/drawer/drawer.js'),
  import(
    '@awesome.me/webawesome-pro/dist/components/dropdown-item/dropdown-item.js'
  ),
  import('@awesome.me/webawesome-pro/dist/components/dropdown/dropdown.js'),
  import('@awesome.me/webawesome-pro/dist/components/icon/icon.js'),
  import('@awesome.me/webawesome-pro/dist/components/input/input.js'),
  import('@awesome.me/webawesome-pro/dist/components/popover/popover.js'),
  import('@awesome.me/webawesome-pro/dist/components/popup/popup.js'),
  import('@awesome.me/webawesome-pro/dist/components/radio/radio.js'),
  import('@awesome.me/webawesome-pro/dist/components/spinner/spinner.js'),
  import(
    '@awesome.me/webawesome-pro/dist/components/split-panel/split-panel.js'
  ),
  import('@awesome.me/webawesome-pro/dist/components/tooltip/tooltip.js'),
  import('./helpers/combobox-open-sync'),
  import('./helpers/radio-group-form-reset')
])

try {
  await componentDefinitions
} finally {
  cleanupPinnedWebAwesomeUpdateWarnings()
}
await syncTreeExpansion(document)
